import { app } from "./firebase-init";
import axios from "api/configApi";
import {
    getMessaging,
    getToken,
    isSupported,
    onMessage,
    Messaging,
} from "firebase/messaging";

const STORAGE_KEY = "FCM_TOKEN";
const DEVICE_ID_KEY = "SHELTER_WEB_DEVICE_ID";

/** Stable browser device id (persisted in localStorage). */
export function getWebDeviceId(): string {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
        const bytes = new Uint8Array(8);
        crypto.getRandomValues(bytes);
        deviceId = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
        localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
}

async function getMessagingIfSupported(): Promise<Messaging | null> {
    try {
        const supported = await isSupported();
        if (!supported) return null;
        return getMessaging(app);
    } catch {
        return null;
    }
}

const FCM_SW_URL = "/firebase-messaging-sw.js";
const FCM_SW_SCOPE = "/firebase-cloud-messaging-push-scope";

async function waitForServiceWorkerActivation(
    registration: ServiceWorkerRegistration
): Promise<ServiceWorkerRegistration> {
    const worker =
        registration.active ||
        registration.waiting ||
        registration.installing;

    if (!worker) {
        throw new Error("[FCM] No service worker found in registration.");
    }

    if (worker.state === "activated") {
        return registration;
    }

    await new Promise<void>((resolve, reject) => {
        const timeout = window.setTimeout(() => {
            worker.removeEventListener("statechange", onStateChange);
            reject(new Error("[FCM] Service worker activation timed out."));
        }, 10000);

        const onStateChange = () => {
            if (worker.state === "activated") {
                window.clearTimeout(timeout);
                worker.removeEventListener("statechange", onStateChange);
                resolve();
            }
        };

        worker.addEventListener("statechange", onStateChange);
    });

    return registration;
}

async function ensureServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
    let registration = await navigator.serviceWorker.getRegistration(FCM_SW_SCOPE);

    if (!registration) {
        registration = await navigator.serviceWorker.register(FCM_SW_URL, {
            scope: FCM_SW_SCOPE,
        });
    }

    return waitForServiceWorkerActivation(registration);
}


/** Returns FCM token when notification permission is already granted. */
export async function getFcmToken(options?: {
    forceRefresh?: boolean;
}): Promise<string | null> {
    if (Notification.permission !== "granted") {
        console.warn("[FCM] Notification permission is not granted.");
        return null;
    }

    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached && !options?.forceRefresh) {
        return cached;
    }

    const messaging = await getMessagingIfSupported();
    if (!messaging) {
        console.warn("[FCM] Firebase Messaging is not supported in this browser.");
        return null;
    }

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
        console.warn("[FCM] Missing REACT_APP_FIREBASE_VAPID_KEY.");
        return null;
    }

    try {
        const swRegistration = await ensureServiceWorkerRegistration();
        const token = await getToken(messaging, {
            vapidKey,
            serviceWorkerRegistration: swRegistration,
        });

        return token || null;
    } catch (error) {
        // Retry once — SW may not be active yet or push storage may be stale after SW unregister.
        try {
            await new Promise((resolve) => setTimeout(resolve, 500));
            const swRegistration = await ensureServiceWorkerRegistration();
            const token = await getToken(messaging, {
                vapidKey,
                serviceWorkerRegistration: swRegistration,
            });
            return token || null;
        } catch (retryError) {
            console.error("[FCM] getToken failed:", retryError);
            return null;
        }
    }
}

export async function requestNotificationPermission(): Promise<
    NotificationPermission | "unsupported"
> {
    if (typeof window === "undefined" || !("Notification" in window)) {
        return "unsupported";
    }
    if (Notification.permission !== "default") {
        if (Notification.permission === "granted") {
            try {
                await ensureServiceWorkerRegistration();
            } catch {
                // SW registration can fail silently here; getFcmToken will retry later.
            }
        }
        return Notification.permission;
    }

    const permission = await Notification.requestPermission();
    if (permission === "granted") {
        try {
            await ensureServiceWorkerRegistration();
        } catch {
            // getFcmToken will retry when login calls registerDeviceToken.
        }
    }
    return permission;
}

export async function registerDeviceToken(options?: { forceRefresh?: boolean }) {
    const token = await getFcmToken(options);
    console.log("FCM token", token);
    if (!token) return;

    try {
        const res = await axios.post("/auth/register-device", {
            token,
            platform: "WEB",
            deviceId: getWebDeviceId(),
        });
        localStorage.setItem(STORAGE_KEY, token);
        return res.data;
    } catch (error) {
        const err = error as { response?: { data?: unknown } };
        return err.response?.data;
    }
}

export async function removeDeviceToken() {
    const token = await getFcmToken();
    if (!token) {
        localStorage.removeItem(STORAGE_KEY);
        return;
    }
    try {
        const res = await axios.post("/auth/remove-device", {
            token,
            platform: "WEB",
            deviceId: getWebDeviceId(),
        });
        localStorage.removeItem(STORAGE_KEY);
        return res.data;
    } catch (error) {
        const err = error as { response?: { data?: unknown } };
        return err.response?.data;
    }
}

/**
 * Get FCM token (if permission granted) and register it for this browser device.
 */
export async function registerWebDevice(options?: {
    forceRefresh?: boolean;
}) {
    const data = await registerDeviceToken(options);
    if (!data) {
        return { registered: false as const };
    }
    return {
        registered: true as const,
        deviceId: getWebDeviceId(),
        data,
    };
}

export const FCM_CONGRATS_EVENT = "shelter:fcm-congrats";

function emitFcmCongrats(message: string) {
    window.dispatchEvent(
        new CustomEvent(FCM_CONGRATS_EVENT, { detail: { message } })
    );
}

/**
 * Log FCM messages received while the app is in the foreground.
 */
export function initForegroundMessageListener() {
    const messaging = getMessaging(app);
    onMessage(messaging, (payload) => {
        console.log("[FCM] Foreground message:", payload);
        if (payload.data?.type === "badge_earned") {
            emitFcmCongrats(payload.data.body);
        }
    });
}

